/**
 * Lesson flow display extras — [BUT-843, ours].
 *
 * The lesson flow's booking IS the URL search params (`LESSON_SEARCH_PARAMS`
 * in `./LessonFlowContext`) — that's what survives a reload and a
 * back/forward nav. This module carries only DISPLAY sugar (names, photos)
 * that would otherwise need a re-fetch, under its OWN prefix
 * (`web-portal-2:lesson`) so it can never collide with the standard booking
 * flow's `web-portal-2:booking` prefix (`../booking/context/sessionStorage.ts`).
 *
 * **Synchronous by design**, unlike booking's `browserAdapter.session`
 * wrapper: `loadLessonDisplayExtras` seeds a lazy `useState` initializer
 * alongside the URL-hydrated selection (`LessonFlowContext.tsx`), and a
 * `Promise` cannot back a synchronous initializer. These extras are
 * non-load-bearing — the context never trusts them without first checking
 * they still describe the CURRENT selection — so skipping
 * `browserAdapter`'s iframe parent-bridge fallback is an acceptable trade
 * here. Mirrors the mobile counterpart's `lessonBookingSession.ts`, which
 * makes the same choice for the same reason.
 *
 * @module lessons/context/sessionStorage
 */

import type { ImageData } from '@lgc/database/types/domains/storage';

const STORAGE_PREFIX = 'web-portal-2:lesson';
const EXTRAS_KEY = `${STORAGE_PREFIX}:displayExtras`;

export interface LessonDisplayExtras {
  instructorId?: number;
  instructorName?: string;
  instructorImage?: ImageData | null;
  bayId?: number;
  bayName?: string;
}

/** Fails silently — quota exceeded or storage unavailable. The URL is the booking. */
export function saveLessonDisplayExtras(extras: LessonDisplayExtras): void {
  try {
    sessionStorage.setItem(EXTRAS_KEY, JSON.stringify(extras));
  } catch {
    // Fail silently.
  }
}

export function loadLessonDisplayExtras(): LessonDisplayExtras | null {
  try {
    const raw = sessionStorage.getItem(EXTRAS_KEY);
    return raw ? (JSON.parse(raw) as LessonDisplayExtras) : null;
  } catch {
    return null;
  }
}

export function clearLessonDisplayExtras(): void {
  try {
    sessionStorage.removeItem(EXTRAS_KEY);
  } catch {
    // Fail silently.
  }
}
